import React, { useRef, useEffect } from 'react';
import { Radio, Square, Volume2 } from 'lucide-react';
import type { ConversationMessage } from '../hooks/useSession';

interface WatchModeViewProps {
  isListening: boolean;
  conversationHistory: ConversationMessage[];
  onStartListening: () => void;
  onStopListening: () => void;
  isSyncing?: boolean;
}

const WatchModeView: React.FC<WatchModeViewProps> = ({
  isListening,
  conversationHistory,
  onStartListening,
  onStopListening,
  isSyncing,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const transcriptMessages = conversationHistory.filter(
    (m) => m.type === 'transcript' || m.type === 'user'
  );

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptMessages.length]);

  if (!isListening && transcriptMessages.length === 0) {
    // Not started yet — show the start button
    return (
      <div className="ap-watch-idle">
        <div className="ap-watch-icon">
          <Radio size={48} strokeWidth={1.5} />
        </div>
        <h2 className="ap-watch-title">Watch Mode</h2>
        <p className="ap-watch-desc">
          ArcPilot will silently listen to a live conversation and build a
          process map from what it hears. No AI responses — just passive capture.
        </p>
        <button className="ap-watch-start-btn" onClick={onStartListening}>
          <Radio size={20} />
          Start Listening
        </button>
        <div className="ap-watch-tips">
          <p>Place your device near the conversation.</p>
          <p>The transcript and process map will build in real time.</p>
        </div>
      </div>
    );
  }

  // Active or completed — show transcript
  return (
    <div className="ap-watch-active">
      {/* Status bar */}
      <div className="ap-watch-status">
        <div className="ap-watch-status-left">
          {isListening ? (
            <>
              <span className="ap-watch-pulse" />
              <Volume2 size={14} />
              <span>Listening...</span>
            </>
          ) : (
            <span className="ap-watch-stopped">Session ended</span>
          )}
        </div>
        <div className="ap-watch-status-right">
          {isSyncing && <span className="ap-syncing-badge">Syncing...</span>}
          <span className="ap-watch-count">{transcriptMessages.length} segments</span>
          {isListening && (
            <button className="ap-watch-stop-btn" onClick={onStopListening}>
              <Square size={14} />
              Stop
            </button>
          )}
        </div>
      </div>

      {/* Transcript */}
      <div className="ap-watch-transcript">
        {transcriptMessages.map((msg, idx) => (
          <div key={idx} className="ap-watch-segment">
            <span className="ap-watch-time">{msg.timestamp}</span>
            <p className="ap-watch-text">{msg.text}</p>
          </div>
        ))}
        {isListening && (
          <div className="ap-watch-segment ap-watch-segment-live">
            <span className="ap-watch-pulse-small" />
            <p className="ap-watch-text ap-watch-text-live">Waiting for speech...</p>
          </div>
        )}
        <div ref={scrollRef} />
      </div>
    </div>
  );
};

export default WatchModeView;
